import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Flag, PauseCircle, PlayCircle, CaretDown, CaretUp } from "@phosphor-icons/react";

function fmtMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

const STATE_LABELS = {
  signup: { label: "In progress", cls: "bg-[#9CA3AF] text-white" },
  profile_complete: { label: "Awaiting review", cls: "bg-amber-500 text-white" },
  qualified: { label: "Bounty earned", cls: "bg-[#0044FF] text-white" },
  tier_upgraded: { label: "Tier upgraded", cls: "bg-violet-600 text-white" },
  first_job_bonus_earned: { label: "First job complete", cls: "bg-emerald-700 text-white" },
  closed: { label: "Closed", cls: "bg-[#4B5563] text-white" },
  already_in_network: { label: "Already in network", cls: "bg-[#4B5563] text-white" },
  blocked: { label: "Self-referral blocked", cls: "bg-red-600 text-white" },
  clawed_back: { label: "Clawed back", cls: "bg-red-700 text-white" },
};

function StateBadge({ state }) {
  const s = STATE_LABELS[state] || STATE_LABELS.signup;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

function RecruitDetail({ vaUserId }) {
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/recruiting/vps/${vaUserId}/recruits`);
      setItems(data.items);
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [vaUserId]);

  const clawback = async (r) => {
    const reason = window.prompt(
      `Clawback both commission events for ${r.worker_name}?\nEnter the fraud/misrepresentation reason:`
    );
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/admin/recruiting/recruits/${r.recruit_id}/clawback`, { reason: reason.trim() });
      toast.success("Commissions reversed");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  if (!items) return <div className="p-4 font-mono-label">Loading recruits…</div>;
  if (items.length === 0) return <div className="p-4 text-sm text-[#4B5563]">No recruits.</div>;

  return (
    <table className="w-full text-xs" data-testid={`recruit-detail-${vaUserId}`}>
      <thead className="bg-[#F3F4F6]">
        <tr className="text-left font-mono uppercase text-[9px] tracking-widest text-[#4B5563]">
          <th className="px-4 py-2">Worker</th>
          <th className="px-4 py-2">Email</th>
          <th className="px-4 py-2">Signed up</th>
          <th className="px-4 py-2">Status</th>
          <th className="px-4 py-2">Tier</th>
          <th className="px-4 py-2 text-right">Accrued</th>
          <th className="px-4 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.recruit_id} className="border-t border-[#E5E7EB]" data-testid={`admin-recruit-row-${r.recruit_id}`}>
            <td className="px-4 py-2 font-semibold">{r.worker_name}</td>
            <td className="px-4 py-2 text-[#4B5563]">{r.worker_email}</td>
            <td className="px-4 py-2 text-[#4B5563]">{(r.created_at || "").slice(0, 10)}</td>
            <td className="px-4 py-2"><StateBadge state={r.state} /></td>
            <td className="px-4 py-2 font-mono">{r.tier ? `T${r.tier}` : "—"}</td>
            <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(r.earned)}</td>
            <td className="px-4 py-2 text-right">
              {r.earned > 0 && !r.clawed_back_at && (
                <button
                  data-testid={`clawback-btn-${r.recruit_id}`}
                  onClick={() => clawback(r)}
                  className="border border-red-600 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-red-600 hover:bg-red-50"
                >
                  Clawback
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AdminRecruiting() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/recruiting/report");
      setRows(data.rows);
    } catch (e) {
      setErr(getErr(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePause = async (r) => {
    try {
      await api.post(`/admin/recruiting/vps/${r.va_user_id}/pause`, { paused: !r.paused });
      toast.success(r.paused ? "Recruiting link re-enabled" : "Recruiting link paused");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-recruiting">
      <div className="mb-6">
        <div className="font-mono-label">VA Program</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Recruiting audit</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
          Per-VP worker-recruitment report — signups, activation rate, tier mix, and accrued bounties/bonuses.
          VPs with 10+ signups and under 20% activation are flagged automatically.
        </p>
      </div>

      {err && (
        <div className="mb-6 border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-recruiting-error">
          {err}
        </div>
      )}

      {!rows ? (
        <div className="font-mono-label">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 border border-dashed border-[#E5E7EB] bg-white p-12 text-center" data-testid="admin-recruiting-empty">
          <UserPlus size={36} weight="duotone" className="text-[#4B5563]" />
          <div className="font-display text-lg font-black">No recruits yet</div>
          <div className="max-w-md text-sm text-[#4B5563]">
            When VPs start sharing their recruit links, per-VP stats will appear here.
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-[#4B5563]">
                <th className="px-4 py-3">VP</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3 text-right">Signups</th>
                <th className="px-4 py-3 text-right">Activated</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3">Tier mix</th>
                <th className="px-4 py-3 text-right">Bounties</th>
                <th className="px-4 py-3 text-right">Bonuses</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.va_user_id}>
                  <tr className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`recruiting-vp-row-${r.va_user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.va_name}</div>
                      <div className="text-xs text-[#4B5563]">{r.va_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.recruit_code || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.signups}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.activated}</td>
                    <td className="px-4 py-3 text-right font-mono">{Math.round((r.activation_rate || 0) * 100)}%</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      T1:{r.tier_mix["1"]} · T2:{r.tier_mix["2"]} · T3:{r.tier_mix["3"]}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(r.bounty_total)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(r.bonus_total)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.flags.includes("velocity") && (
                          <span className="inline-flex items-center gap-1 bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" data-testid={`velocity-flag-${r.va_user_id}`}>
                            <Flag size={10} weight="fill" /> Velocity
                          </span>
                        )}
                        {r.flags.includes("blocked_signups") && (
                          <span className="bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                            {r.blocked} blocked
                          </span>
                        )}
                        {r.paused && (
                          <span className="bg-[#4B5563] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                            Paused
                          </span>
                        )}
                        {r.clawbacks > 0 && (
                          <span className="bg-red-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                            {r.clawbacks} clawback{r.clawbacks > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`pause-recruiting-btn-${r.va_user_id}`}
                          onClick={() => togglePause(r)}
                          className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                            r.paused
                              ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                              : "border-amber-600 text-amber-700 hover:bg-amber-50"
                          }`}
                        >
                          {r.paused ? <PlayCircle size={12} /> : <PauseCircle size={12} />}
                          {r.paused ? "Resume" : "Pause"}
                        </button>
                        <button
                          data-testid={`expand-recruits-btn-${r.va_user_id}`}
                          onClick={() => setExpanded(expanded === r.va_user_id ? null : r.va_user_id)}
                          className="inline-flex items-center gap-1 border border-[#030712] px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-[#F9FAFB]"
                        >
                          {expanded === r.va_user_id ? <CaretUp size={12} /> : <CaretDown size={12} />}
                          Recruits
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === r.va_user_id && (
                    <tr className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
                      <td colSpan={10} className="p-0">
                        <RecruitDetail vaUserId={r.va_user_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
